/**
 * Conversation export utility – CSV, Excel, JSON
 * Exports with conversation_id, contact info, user input, agent/AI response, etc.
 */
import * as XLSX from 'xlsx';

export interface ExportMessage {
  id: number;
  content: string;
  message_type: 'incoming' | 'outgoing' | 'activity';
  content_type?: string;
  sender?: { name: string };
  sender_type?: 'user' | 'agent' | 'ai' | 'bot';
  created_at: string;
  private?: boolean;
}

export interface ExportConversation {
  id: number;
  display_id: number;
  status: string;
  contact: { id: number; name: string; email?: string; phone_number?: string };
  assignee?: { id: number; full_name: string };
  channel?: { name: string; channel_type: string };
  created_at?: string;
  last_activity_at?: string;
}

export interface ExportRow {
  conversation_id: number;
  display_id: number;
  contact_id: number;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  channel_name: string;
  channel_type: string;
  conversation_status: string;
  assignee_name: string;
  message_id: number;
  message_created_at: string;
  message_type: string;
  sender_type: string;
  sender_name: string;
  is_user_input: string;
  is_agent_or_ai_response: string;
  content: string;
  private_message: string;
}

function messageToRow(conv: ExportConversation, msg: ExportMessage): ExportRow {
  const isIncoming = msg.message_type === 'incoming';
  const senderType = msg.sender_type || (isIncoming ? 'user' : 'agent');
  const senderName = msg.sender?.name ?? (isIncoming ? conv.contact?.name : conv.assignee?.full_name ?? 'Agent');

  return {
    conversation_id: conv.id,
    display_id: conv.display_id,
    contact_id: conv.contact?.id ?? 0,
    contact_name: conv.contact?.name ?? '',
    contact_email: conv.contact?.email ?? '',
    contact_phone: conv.contact?.phone_number ?? '',
    channel_name: conv.channel?.name ?? '',
    channel_type: conv.channel?.channel_type ?? '',
    conversation_status: conv.status ?? '',
    assignee_name: conv.assignee?.full_name ?? '',
    message_id: msg.id,
    message_created_at: msg.created_at ?? '',
    message_type: msg.message_type,
    sender_type: senderType,
    sender_name: senderName,
    is_user_input: isIncoming ? 'Yes' : 'No',
    is_agent_or_ai_response: !isIncoming ? 'Yes' : 'No',
    content: (msg.content || '').replace(/\r?\n/g, ' ').trim(),
    private_message: msg.private ? 'Yes' : 'No',
  };
}

export function buildExportRows(conversations: Array<{ conversation: ExportConversation; messages: ExportMessage[] }>): ExportRow[] {
  const rows: ExportRow[] = [];
  for (const { conversation, messages } of conversations) {
    const sorted = [...messages].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
    for (const msg of sorted) {
      if (msg.message_type === 'activity') continue; // optional: include activity or skip
      rows.push(messageToRow(conversation, msg));
    }
  }
  return rows;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}

const CSV_HEADERS: (keyof ExportRow)[] = [
  'conversation_id', 'display_id', 'contact_id', 'contact_name', 'contact_email', 'contact_phone',
  'channel_name', 'channel_type', 'conversation_status', 'assignee_name', 'message_id', 'message_created_at',
  'message_type', 'sender_type', 'sender_name', 'is_user_input', 'is_agent_or_ai_response', 'content', 'private_message'
];

export function exportToCSV(rows: ExportRow[], filenameBase: string) {
  const BOM = '\uFEFF';
  const csvContent = BOM + CSV_HEADERS.join(',') + '\n' + rows.map((r) =>
    CSV_HEADERS.map((h) => `"${String(r[h] ?? '').replace(/"/g, '""')}"`).join(',')
  ).join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filenameBase}.csv`);
}

export function exportToExcel(rows: ExportRow[], filenameBase: string) {
  const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [{}]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Conversations');
  XLSX.writeFile(wb, `${filenameBase}.xlsx`);
}

export function exportToJSON(rows: ExportRow[], filenameBase: string) {
  const json = JSON.stringify(rows, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  downloadBlob(blob, `${filenameBase}.json`);
}

export type ExportFormat = 'csv' | 'excel' | 'json';

export function exportConversations(
  rows: ExportRow[],
  filenameBase: string,
  format: ExportFormat
) {
  const safeName = filenameBase.replace(/[^a-zA-Z0-9_-]/g, '_');
  switch (format) {
    case 'csv':
      exportToCSV(rows, safeName);
      break;
    case 'excel':
      exportToExcel(rows, safeName);
      break;
    case 'json':
      exportToJSON(rows, safeName);
      break;
    default:
      exportToCSV(rows, safeName);
  }
}
